import Head from 'next/head'

import { getMetaTagsData } from '@/components/landings/util/helper'
import {
  BreadCrumbTypes,
  HeadSeoPropsTypes,
  ImageObjectTypes,
} from '@/types/index'

const HeadSeo = ({
  breed,
  variables,
  landingType,
  citySlug,
}: HeadSeoPropsTypes) => {
  // Meta tags values
  const { title, description, url, image, name, slug } = getMetaTagsData(
    breed,
    variables,
    landingType,
    citySlug
  )
  const ROOT_URL = process?.env?.NEXT_PUBLIC_ROOT_URL

  // Structured data
  const breadCrumbSD: BreadCrumbTypes = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: ROOT_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Puppies for Sale',
        item: ROOT_URL + '/puppies-for-sale',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name,
        item: ROOT_URL + '/puppies-for-sale/' + slug,
      },
    ],
  }

  const imageObj: ImageObjectTypes = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: image,
    caption: `Adorable ${name} puppy`,
    representativeOfPage: 'True',
    name: `${name} dog`,
    description: `${name} puppy for sale by Mawoo Pets`,
    width: 391,
    height: 370,
  }

  switch (landingType) {
    case 'breedState':
      breadCrumbSD.itemListElement.push({
        '@type': 'ListItem',
        position: 4,
        name: variables?.stateName,
      })
      imageObj.contentLocation = variables?.stateName
      // Fallthrough to 'state' case
      break
    case 'breedCity':
      breadCrumbSD.itemListElement.push({
        '@type': 'ListItem',
        position: 4,
        name: citySlug,
      })
      imageObj.contentLocation = variables?.stateName
      break
    // Fallthrough to 'state' case
    case 'state':
      imageObj.contentLocation = variables?.stateName
      break
    default:
      break
  }

  return (
    <Head>
      {/*  Facebook tags  */}
      <meta property="og:title" content={title} key="title" />
      <meta name="og:description" content={description} key="description" />
      <meta property="og:type" content="website" key="type" />
      <meta property="og:url" content={url} key="url" />
      <meta property="og:image" content={image} key="image" />
      <meta property="og:image:alt" content="Search image" key="imageAlt" />
      <meta property="og:locale" content="en_US" key="locale" />
      {/*  twitter tags  */}
      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitterCard"
      />
      <meta name="twitter:title" content={title} key="twitterTitle" />
      <meta
        name="twitter:description"
        content={description}
        key="twitterDescription"
      />
      <meta name="twitter:image" content={image} key="twitterImage" />
      {/*  standard tags  */}
      <title>{title}</title>
      <link rel="canonical" href={url} />
      <meta name="description" content={description} />
      <meta charSet="UTF-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1"
      />
      <meta
        name="robots"
        content={
          ROOT_URL === 'https://www.mawoopets.com'
            ? 'index, follow'
            : 'noindex, nofollow'
        }
      />

      <script
        key="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadCrumbSD) }}
      />
      <script
        key="structured-data-image"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(imageObj) }}
      />
    </Head>
  )
}

export default HeadSeo
